import React, { useContext, useEffect } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { RestaurantNotificationEventType } from 'types';
import {
	INotificationEvent,
	IPushNotificationPayload,
	IUploadApkNotificationPayload,
	IUploadFirmwareNotificationPayload,
	IUploadGameAvatarNotificationPayload,
	IUploadGameFileNotificationPayload,
	IUploadVideoAvatarNotificationPayload,
	IUploadVideoFileNotificationPayload,
	TNotificationEventStatuses,
} from 'components/ServerNotifications/types';
import { notification } from 'antd';
import Text from 'components/Text/Text';
import style from './ServerNotifications.module.scss';

let isFirstEntry = true;
let successKeys: string[] = [];
let errorKeys: string[] = [];

const ServerNotifications: React.FC = () => {
	const { notificationsConnection, loggedUser } = useContext<IAppContext>(AppContext);

	const NOTIFICATION_EVENTS = {
		GAME_AVATAR_UPLOADED: `user-${loggedUser.userId}-game-avatar-uploaded`,
		GAME_FILE_UPLOADED: `user-${loggedUser.userId}-game-file-uploaded`,
		VIDEO_AVATAR_UPLOADED: `user-${loggedUser.userId}-video-avatar-uploaded`,
		VIDEO_FILE_UPLOADED: `user-${loggedUser.userId}-video-file-uploaded`,
		FIRMWARE_UPLOADED: `user-${loggedUser.userId}-firmware-uploaded`,
		TABLET_APP_UPLOADED: `user-${loggedUser.userId}-tablet-app-uploaded`,
		PUSH_NOTIFICATION: `user-${loggedUser.userId}-push-notification-finished`,
	};

	useEffect(() => {
		if (notificationsConnection && loggedUser.userId && isFirstEntry) {
			isFirstEntry = false;

			const startNotificationObserver = async () => {
				const renderMessage = (
					status: TNotificationEventStatuses,
					successTitle: string,
					successDescription: string,
					errorDescription: string,
					descriptionTranslationOptions?: Record<string, string>,
					errorTitle?: string,
				) => {
					const successStatus = status === 'Finished' || status === 'Completed';
					const key = `notification-${Date.now()}`;
					const keys = successStatus ? successKeys : errorKeys;
					keys.push(key);

					const handleCloseGroup = () => {
						keys.forEach((k) => notification.close(k));
						if (successStatus) {
							successKeys = [];
						} else {
							errorKeys = [];
						}
					};

					notification.open({
						key,
						message: (
							<Text
								translationNameSpace="notifications"
								translationKey={successStatus ? successTitle : errorTitle || 'errorLoadingTitle'}
								asFragment={true}
							/>
						),
						description: (
							<Text
								translationNameSpace="notifications"
								translationKey={successStatus ? successDescription : errorDescription}
								translationOptions={descriptionTranslationOptions}
								asFragment={true}
							/>
						),
						btn: (
							<span
								className="text-xs opacity-50 hover:opacity-100 cursor-pointer"
								onClick={handleCloseGroup}>
								<Text
									translationNameSpace="notifications"
									translationKey={successStatus ? 'closeAllSuccess' : 'closeAllErrors'}
									asFragment={true}
								/>
							</span>
						),
						duration: null,
						className: style[successStatus ? 'green' : 'red'],
						onClose: () => {
							const arr = successStatus ? successKeys : errorKeys;
							const idx = arr.indexOf(key);
							if (idx !== -1) {
								arr.splice(idx, 1);
							}
						},
					});
				};

				try {
					await notificationsConnection.start();

					notificationsConnection.on(
						NOTIFICATION_EVENTS.GAME_AVATAR_UPLOADED,
						({
							payload: { uploadStatus, name },
						}: INotificationEvent<IUploadGameAvatarNotificationPayload>) => {
							renderMessage(
								uploadStatus,
								'gameAvatarTitle',
								'gameAvatarSuccessDescription',
								'gameAvatarErrorDescription',
								{ name },
							);
						},
					);
					notificationsConnection.on(
						NOTIFICATION_EVENTS.GAME_FILE_UPLOADED,
						({
							payload: { uploadStatus, name, restaurant },
						}: INotificationEvent<IUploadGameFileNotificationPayload>) => {
							renderMessage(
								uploadStatus,
								'gameFileTitle',
								'gameFileSuccessDescription',
								'gameFileErrorDescription',
								{ name, restaurantName: restaurant.name },
							);
						},
					);
					notificationsConnection.on(
						NOTIFICATION_EVENTS.VIDEO_FILE_UPLOADED,
						({
							payload: { uploadStatus, name, restaurant },
						}: INotificationEvent<IUploadVideoFileNotificationPayload>) => {
							renderMessage(
								uploadStatus,
								'videoFileTitle',
								'videoFileSuccessDescription',
								'videoFileErrorDescription',
								{ name, restaurantName: restaurant.name },
							);
						},
					);
					notificationsConnection.on(
						NOTIFICATION_EVENTS.VIDEO_AVATAR_UPLOADED,
						({
							payload: { uploadStatus, name, restaurant },
						}: INotificationEvent<IUploadVideoAvatarNotificationPayload>) => {
							renderMessage(
								uploadStatus,
								'videoAvatarTitle',
								'videoAvatarSuccessDescription',
								'videoAvatarErrorDescription',
								{ name, restaurantName: restaurant.name },
							);
						},
					);
					notificationsConnection.on(
						NOTIFICATION_EVENTS.FIRMWARE_UPLOADED,
						({
							payload: { uploadStatus, name, restaurant },
						}: INotificationEvent<IUploadFirmwareNotificationPayload>) => {
							renderMessage(
								uploadStatus,
								'firmwareFileSuccessTitle',
								'firmwareFileSuccessDescription',
								'firmwareFileErrorDescription',
								{ name, restaurantName: restaurant.name },
							);
						},
					);
					notificationsConnection.on(
						NOTIFICATION_EVENTS.TABLET_APP_UPLOADED,
						({
							payload: { uploadStatus, name, restaurant },
						}: INotificationEvent<IUploadApkNotificationPayload>) => {
							renderMessage(
								uploadStatus,
								'tabletAppFileSuccessTitle',
								'tabletAppFileSuccessDescription',
								'tabletAppFileErrorDescription',
								{ name, restaurantName: restaurant.name },
							);
						},
					);
					notificationsConnection.on(
						NOTIFICATION_EVENTS.PUSH_NOTIFICATION,
						({
							payload: { status, restaurant, notificationType },
						}: INotificationEvent<IPushNotificationPayload>) => {
							if (
								notificationType === RestaurantNotificationEventType.SynchronizationMenu ||
								notificationType === RestaurantNotificationEventType.SyncRestaurantMenu
							) {
								return renderMessage(
									status,
									'syncMenuSuccessTitle',
									'syncMenuSuccessDescription',
									'syncMenuErrorDescription',
									{ restaurantName: restaurant.name },
									'syncMenuErrorTitle',
								);
							}
						},
					);
				} catch (e) {
					console.log('Connection failed: ', e);
				}
			};

			startNotificationObserver();
		}
	}, [notificationsConnection, loggedUser.userId]);

	return <></>;
};

export default ServerNotifications;
